import { constructFormData } from 'api/attachments/construct-form-data';
import { HttpClient } from 'api/http-client';
import { Company, CompanyAddress, CompanyType } from 'api/models';
import { Minutes } from 'components/organisms/user-capacity/types';

export type CreateCompanyPayload = Partial<
  Omit<Company, 'companyId' | 'companyType'>
>;
export type UpdateCompanyPayload = CreateCompanyPayload;
export type CompanyLogoResponse = Pick<
  Company,
  'companyLogoUrl' | 'companyUrl'
>;
export type CompanyLogoUploadResponse = {
  location: string;
  thumbnailLocation: string;
};

class CompanyHttpClient extends HttpClient {
  public async updateCompany(
    companyId: number,
    data: UpdateCompanyPayload,
  ): Promise<Company> {
    return this.put(`/companies/${companyId}`, data);
  }

  public async createCompany(payload: CreateCompanyPayload): Promise<{
    companyName: string;
    companyLogoUrl: string;
    companyId: number;
    capacityMinutes: Minutes;
    companyType: CompanyType;
  }> {
    return this.post('/companies', payload);
  }

  public async getAllCompanies(): Promise<Company[]> {
    return this.get<Company[]>('/companies');
  }

  /**
   * example: getCompanyLogoUrl('nike.com')
   * @param name
   */
  public async getCompanyLogoUrl(name: string): Promise<CompanyLogoResponse> {
    return this.get('/companies/info', { params: { name } });
  }

  public async uploadPicture(file: File): Promise<CompanyLogoUploadResponse> {
    const formData = constructFormData(file, {});

    return this.post<FormData, FormData, CompanyLogoUploadResponse>(
      '/companies/photo',
      formData,
    );
  }

  public exportAsCSV = (
    companyId: number,
    columnOrder: string[],
  ): Promise<string> => {
    return this.post<{ downloadUrl: string }, { columnOrder: string[] }>(
      `reports/company-people/export?companyId=${companyId}`,
      { columnOrder },
    ).then((response) => response.downloadUrl);
  };

  public async getCompanyAddresses(
    companyId: number,
    source: 'ACCOUNT' | 'INVOICE',
  ): Promise<CompanyAddress[]> {
    return this.get(`/companies/${companyId}/address?source=${source}`);
  }

  public getSpecificCompanyAddress(
    companyId: number,
    companyAddressId: number,
    source: 'ACCOUNT' | 'INVOICE',
  ): Promise<CompanyAddress> {
    return this.get(
      `/companies/${companyId}/address/${companyAddressId}?source=${source}`,
    );
  }

  public async createAddress(
    companyId: number,
    payload: Omit<CompanyAddress, 'companyAddressId'>,
  ): Promise<CompanyAddress> {
    return this.post(`/companies/${companyId}/address`, payload);
  }

  public async editAddress(
    companyId: number,
    companyAddressId: number,
    payload: Omit<CompanyAddress, 'companyAddressId'>,
  ): Promise<void> {
    return this.put(
      `/companies/${companyId}/address/${companyAddressId}`,
      payload,
    );
  }

  public async deleteAddress(
    companyId: number,
    companyAddressId: number,
  ): Promise<CompanyAddress> {
    return this.delete(`/companies/${companyId}/address/${companyAddressId}`);
  }
}

export const CompanyAPI = new CompanyHttpClient();
